import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { PageHeader } from "../../components/dash/ui";
import { Button } from "../../components/ui/button";
import { ReasonDialog } from "../../components/dash/ReasonDialog";
import { LandMapView, Legend, STATE_COLORS, EXCLUSION_COLOR } from "../../components/map/LandMap";
import { formatRupiah, formatDay } from "../../lib/labels";
import { AreaStatePill, CapacitySummary } from "./LandProjects";
import { n, fileUrl, LAND_CATEGORY_SHORT } from "./shared";
import { PlantingSummary, InvestorPlantingChoice } from "../../components/PlantingSystem";
import { ClimateView } from "../../components/Elevation";
import { RainCalendar } from "../../components/RainCalendar";
import { SuitabilityView, SuitabilityBadge, isFit, EvidenceList, ReportButton, ShareReport } from "../../components/Suitability";
import { AIInspectorSummary } from "../../components/AIInspector";

const pubPhoto = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/public/land-files/${id}`;

const MyBookings = ({ rows, reload }) => {
  const [target, setTarget] = useState(null);
  if (!rows.length) return null;
  return (
    <section className="bg-white border border-slate-200/80 rounded-xl p-4 sm:p-6 mb-6" data-testid="my-bookings">
      <h2 className="text-base md:text-lg font-bold mb-3">Booking Saya</h2>
      <div className="divide-y divide-slate-100">
        {rows.map((b) => (
          <div key={b.id} className="py-2 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`my-booking-${b.id}`}>
            <span><b className="font-mono">{b.projectCode}</b> · lahan {b.landCode} · {n(b.trees)} batang</span>
            <span className="flex items-center gap-2">
              <span className="text-xs font-semibold" data-testid={`my-booking-status-${b.id}`}>{{ ACTIVE: `Aktif s/d ${formatDay(b.expiresAt)}`, CONTRACTED: "Kontrak efektif", EXPIRED: "Kedaluwarsa", CANCELLED: "Dibatalkan", COMPLETED: "Kontrak selesai" }[b.status] || b.status}</span>
              {b.status === "ACTIVE" && <Button size="sm" variant="outline" className="text-red-700" onClick={() => setTarget(b)} data-testid={`my-booking-cancel-${b.id}`}>Batalkan</Button>}
            </span>
          </div>
        ))}
      </div>
      <ReasonDialog open={!!target} onOpenChange={(o) => !o && setTarget(null)} title="Batalkan booking" danger confirmLabel="Batalkan"
        onConfirm={async (reason) => { try { await api.post(`/bookings/${target.id}/cancel`, { reason }); toast.success("Booking dibatalkan"); reload(); } catch (e) { toast.error(formatApiError(e)); return false; } }} />
    </section>
  );
};

function RabNotices() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/investor/notifications").then((r) => setItems(r.data.items.filter((i) => !i.read))).catch(() => {});
  useEffect(() => { load(); }, []);
  const read = (id) => api.post(`/investor/notifications/${id}/read`).then(load).catch(() => {});
  if (!items.length) return null;
  return (
    <div className="mb-4 space-y-2" data-testid="investor-notices">
      {items.map((i) => (
        <div key={i.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-emerald-300 bg-emerald-50 p-3 text-sm" data-testid={`investor-notice-${i.id}`}>
          <span className="text-emerald-900"><b>RAB terbit:</b> {i.text}</span>
          <span className="flex gap-2">
            <Button asChild size="sm" className="bg-emerald-800 hover:bg-emerald-900" data-testid={`investor-notice-open-${i.id}`}><Link to={i.link} onClick={() => read(i.id)}>Lihat & Booking</Link></Button>
            <Button size="sm" variant="outline" onClick={() => read(i.id)} data-testid={`investor-notice-dismiss-${i.id}`}>Tutup</Button>
          </span>
        </div>))}
    </div>
  );
}

export function InvestorLandList() {
  const [lands, setLands] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [sp, setSp] = useSearchParams();
  const onlyFit = sp.get("cocok") === "1";
  const setOnlyFit = (fn) => setSp(fn(onlyFit) ? { cocok: "1" } : {}, { replace: true });
  const load = () => {
    api.get("/investor/lands").then((r) => setLands(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/bookings").then((r) => setBookings(r.data));
  };
  useEffect(load, []);
  const shown = (lands || []).filter((l) => !onlyFit || isFit(l.suitability));
  return (
    <div data-testid="investor-lands-page">
      <PageHeader title="Rencana Kebun & Lahan Tersedia" subtitle="Pilih sendiri area Rencana Kebun berwarna hijau untuk dibooking. Booking mengunci area selama 7 hari." />
      <RabNotices />
      <MyBookings rows={bookings} reload={load} />
      <div className="mb-4"><Legend keys={["FREE", "BOOKED", "INVESTED", "PARTIAL", "COMPLETED"]} /></div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <button type="button" role="switch" aria-checked={onlyFit} onClick={() => setOnlyFit((v) => !v)}
          className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 min-h-[40px] text-sm font-semibold transition-colors ${onlyFit ? "bg-emerald-700 border-emerald-700 text-white" : "bg-white border-slate-300 text-slate-700 hover:border-emerald-600"}`} data-testid="investor-filter-fit">
          <span className={`h-4 w-4 rounded border grid place-items-center text-[10px] ${onlyFit ? "bg-white text-emerald-700 border-white" : "border-slate-400"}`}>{onlyFit ? "✓" : ""}</span>
          Hanya lahan Cocok
        </button>
        {lands && <span className="text-xs text-slate-500" data-testid="investor-filter-count">{shown.length} dari {lands.length} lahan{onlyFit ? " (Skor Kesesuaian ≥ 70: Cocok / Sangat Cocok)" : ""}</span>}
      </div>
      {lands && !lands.length && <p className="text-sm text-slate-500" data-testid="investor-lands-empty">Belum ada lahan yang dipublikasikan.</p>}
      {lands && lands.length > 0 && !shown.length && <p className="text-sm text-slate-500" data-testid="investor-lands-fit-empty">Belum ada lahan dengan Skor Kecocokan "Cocok". Matikan filter untuk melihat semua lahan.</p>}
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {shown.map((l) => (
          <Link to={l.id} key={l.id} className="bg-white border border-slate-200/80 rounded-2xl overflow-hidden hover:border-emerald-600 transition-colors" data-testid={`investor-land-${l.id}`}>
            <div className="h-36 bg-emerald-900 relative">{l.photos[0] && <img src={pubPhoto(l.photos[0])} alt="" className="h-full w-full object-cover" />}
              <span className="absolute top-3 left-3 bg-white/95 font-mono text-xs font-bold px-2 py-1 rounded-md">{l.landCode}</span>
              <span className="absolute top-3 right-3"><AreaStatePill state={l.colorState} testid={`investor-land-state-${l.id}`} /></span></div>
            <div className="p-4 space-y-1">
              <p className="font-bold text-lg">{l.name}</p>
              <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{[l.village, l.regency].filter(Boolean).join(", ")}</p>
              <p className="text-sm">{n(l.areaHectares)} ha · <b className="text-emerald-700">{n(l.availableTrees)} batang tersedia</b> dari {n(l.capacity)}</p>
              <p className="text-xs text-slate-500">Pengelola: {l.farmerGroup?.name}</p>
              <SuitabilityBadge s={l.suitability} testid={`investor-land-fit-${l.id}`} />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function InvestorLandDetail() {
  const { id } = useParams();
  const [l, setL] = useState(null);
  const [target, setTarget] = useState(null);
  const load = useCallback(() => api.get(`/investor/lands/${id}`).then((r) => setL(r.data)).catch((e) => toast.error(formatApiError(e))), [id]);
  useEffect(() => { load(); }, [load]);
  if (!l) return <p className="text-sm text-slate-500">Memuat...</p>;
  const overlays = [{ points: l.boundary, color: STATE_COLORS[l.colorState]?.[0], fill: 0.08, label: "Batas lahan" },
    ...(l.exclusions || []).map((e) => ({ points: e, color: EXCLUSION_COLOR, fill: 0.35, dashed: true, label: "Tidak dapat ditanami" })),
    ...l.projects.filter((p) => p.area).map((p) => ({ points: p.area, color: STATE_COLORS[p.areaState][0], fill: 0.45, label: `${p.projectCode} · ${STATE_COLORS[p.areaState][1]}` }))];
  const book = async () => {
    try { await api.post("/bookings", { projectId: target.id }); toast.success("Booking berhasil. Area dikunci 7 hari."); setTarget(null); load(); }
    catch (e) { toast.error(formatApiError(e)); setTarget(null); load(); }
  };
  return (
    <div className="space-y-6" data-testid="investor-land-detail">
      <Link to=".." relative="path" className="inline-flex items-center gap-1 text-sm text-emerald-800 font-semibold"><ArrowLeft className="h-4 w-4" />Semua lahan</Link>
      <PageHeader title={`${l.landCode} · ${l.name}`} subtitle={`${[l.village, l.district, l.regency, l.province].filter(Boolean).join(", ")} · Pengelola: ${l.farmerGroup?.name || "-"}`}
        action={<AreaStatePill state={l.colorState} testid="investor-detail-state" />} />
      <LandMapView point={l.latitude != null ? [l.latitude, l.longitude] : null} overlays={overlays} height="h-80 sm:h-[26rem]" testid="investor-land-map" />
      <Legend exclusion />
      <CapacitySummary land={l} testid="investor-capacity" />
      <PlantingSummary planting={l.planting} plantingSystem={l.plantingSystem} testid="investor-planting" />
      <InvestorPlantingChoice landId={l.id} effHa={l.effectiveAreaHectares} activeSystem={l.plantingSystem} />
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="investor-report-box">
        <span className="text-emerald-900">Rapor Lahan 1 halaman: skor kesesuaian, kalender tanam & foto bukti lapangan.</span>
        <ReportButton landId={l.id} code={l.landCode} testid="investor-report-pdf-btn" />
        <ShareReport landId={l.id} landName={l.name} />
      </div>
      <SuitabilityView s={l.suitability} testid="investor-suit" />
      <AIInspectorSummary landId={l.id} />
      <EvidenceList landId={l.id} items={l.evidence} canEdit={false} onChange={() => {}} />
      {l.climate && <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm"><ClimateView c={l.climate} testid="investor-climate" /></div>}
      <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm"><RainCalendar cal={l.rainCalendar} testid="investor-rain-calendar" /></div>
      <section className="bg-white border border-slate-200/80 rounded-xl p-4 sm:p-6" data-testid="investor-projects">
        <h2 className="text-base md:text-lg font-bold mb-3">Rencana Kebun / Area di Lahan Ini</h2>
        <div className="grid md:grid-cols-2 gap-3">
          {l.projects.map((p) => (
            <div key={p.id} className="rounded-xl border p-4 space-y-2" style={{ borderColor: STATE_COLORS[p.areaState][0] }} data-testid={`investor-project-${p.id}`}>
              <div className="flex justify-between gap-2"><b className="font-mono">{p.projectCode}</b><AreaStatePill state={p.areaState} testid={`investor-project-state-${p.id}`} /></div>
              <p className="text-sm">{p.name}</p>
              <p className="text-sm"><b>{n(p.targetTrees)} batang</b>{p.areaHectares ? ` · ${n(p.areaHectares)} ha` : ""}</p>
              <p className="text-sm">RAB Final: <b>{p.investmentValue ? formatRupiah(p.investmentValue) : "Belum ditetapkan"}</b> <span className="text-xs text-slate-500">(terpisah dari Estimasi Modal Awal)</span></p>
              {p.bookedByMe && <p className="text-xs font-semibold text-amber-700">Anda sedang membooking area ini</p>}
              {p.areaState === "FREE" && (p.rabPending ? (
                <div className="space-y-1" data-testid={`book-project-rab-pending-${p.id}`}>
                  <Button disabled className="w-full" data-testid={`book-project-${p.id}`}>Menunggu RAB Teknis</Button>
                  <p className="text-xs text-amber-800">Pola Sangat Intensif 3.300/ha dapat dilihat, tetapi belum bisa dibooking sampai PT SJI menetapkan estimasi modal/RAB teknis. Anda akan diberi notifikasi saat RAB terbit.</p>
                </div>
              ) : <Button asChild className="w-full bg-emerald-800 hover:bg-emerald-900" data-testid={`book-project-${p.id}`}><Link to={`../../rencana-kebun/${p.id}`} relative="path">Lihat Rencana Kebun & Nyatakan Minat</Link></Button>)}
            </div>
          ))}
          {!l.projects.length && <p className="text-sm text-slate-500">Pengelola belum membagi lahan menjadi kebun.</p>}
        </div>
      </section>
      {l.photos.length > 0 && <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="investor-land-photos">{l.photos.map((f) => <img key={f} src={pubPhoto(f)} alt="" className="rounded-lg h-32 w-full object-cover" />)}</div>}
      <section className="bg-white border border-slate-200/80 rounded-xl p-4 sm:p-6 text-sm grid grid-cols-2 lg:grid-cols-3 gap-3">
        {[["Kategori", LAND_CATEGORY_SHORT[l.landCategory]], ["Ketinggian", l.elevation != null && `${n(l.elevation)} mdpl`], ["Kemiringan", l.slope], ["Akses jalan", l.accessRoad], ["Sumber air", l.waterAccess], ["Kondisi lahan", l.landCondition]].map(([k, v]) => <p key={k}><span className="text-xs text-slate-500">{k}</span><br /><b>{v || "-"}</b></p>)}
      </section>
      <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs sm:text-sm text-amber-900" data-testid="investor-disclaimer">{l.disclaimer} {l.bookingNote}</p>
      <ReasonDialog open={!!target} onOpenChange={(o) => !o && setTarget(null)} title={`Booking ${target?.projectCode || ""}`} confirmLabel="Booking Sekarang" requireReason={false}
        description={target && `${n(target.targetTrees)} batang${target.investmentValue ? ` · ${formatRupiah(target.investmentValue)}` : ""}. Area dikunci 7 hari kalender. ${l.bookingNote}`} onConfirm={book} />
    </div>
  );
}

export const unusedFileUrl = fileUrl;
